/**
 * File Upload Utility Functions
 * Validation, size checks, mime type detection
 */

// ✅ Max file size (25MB default)
export const DEFAULT_MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB in bytes

// ✅ Accepted mime types by category
// Aligned with R2 server allowed MIME types
export const MIME_TYPES = {
  images: [
    "image/png",
    "image/jpeg",
    "image/jpg", // Alias for jpeg, kept for compatibility
    "image/gif",
    "image/svg+xml",
    "image/webp",
    "image/avif", // Added for R2 compatibility
  ],
  videos: [
    "video/mp4",
    "video/webm",
    "video/quicktime",
    // Note: video/ogg removed - not supported by R2
  ],
  audios: [
    "audio/mpeg",
    "audio/mp3", // Alias for mpeg, kept for compatibility
    "audio/wav",
    "audio/ogg",
    // Note: audio/webm removed - not supported by R2
  ],
  documents: [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel", // Added for R2 compatibility
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // Added for R2 compatibility
    // Note: text/plain removed - not supported by R2
  ],
} as const;

// ✅ All accepted mime types
export const ALL_ACCEPTED_TYPES = [
  ...MIME_TYPES.images,
  ...MIME_TYPES.videos,
  ...MIME_TYPES.audios,
  ...MIME_TYPES.documents,
];

/**
 * Format file size to human readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

/**
 * Validate file size
 */
export const validateFileSize = (
  file: File,
  maxSize: number = DEFAULT_MAX_FILE_SIZE
): { valid: boolean; error?: string } => {
  if (file.size > maxSize) {
    return {
      valid: false,
      error: `Dosya boyutu ${formatFileSize(
        maxSize
      )} limitini aşıyor. (${formatFileSize(file.size)})`,
    };
  }

  return { valid: true };
};

/**
 * Validate file type
 */
export const validateFileType = (
  file: File,
  acceptedTypes: string[] = ALL_ACCEPTED_TYPES
): { valid: boolean; error?: string } => {
  if (!acceptedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Desteklenmeyen dosya tipi: ${file.type}`,
    };
  }

  return { valid: true };
};

/**
 * Comprehensive file validation
 */
export interface FileValidationOptions {
  maxSize?: number;
  acceptedTypes?: string[];
}

export const validateFile = (
  file: File,
  options: FileValidationOptions = {}
): { valid: boolean; error?: string } => {
  const {
    maxSize = DEFAULT_MAX_FILE_SIZE,
    acceptedTypes = ALL_ACCEPTED_TYPES,
  } = options;

  // Size validation
  const sizeValidation = validateFileSize(file, maxSize);
  if (!sizeValidation.valid) {
    return sizeValidation;
  }

  // Type validation
  const typeValidation = validateFileType(file, acceptedTypes);
  if (!typeValidation.valid) {
    return typeValidation;
  }

  return { valid: true };
};

/**
 * Get file category from mime type
 */
export const getFileCategory = (
  mimeType: string
): "image" | "video" | "audio" | "document" | "unknown" => {
  if (MIME_TYPES.images.includes(mimeType as any)) return "image";
  if (MIME_TYPES.videos.includes(mimeType as any)) return "video";
  if (MIME_TYPES.audios.includes(mimeType as any)) return "audio";
  if (MIME_TYPES.documents.includes(mimeType as any)) return "document";
  return "unknown";
};

/**
 * Generate file title from filename
 */
export const generateFileTitle = (file: File): string => {
  // Remove extension
  const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
  // Clean up filename
  return nameWithoutExt.replace(/[_-]/g, " ").trim();
};

/**
 * Create preview URL for file (images, videos)
 */
export const createFilePreview = (file: File): string | null => {
  const category = getFileCategory(file.type);

  if (category === "image" || category === "video") {
    return URL.createObjectURL(file);
  }

  return null;
};

/**
 * Revoke file preview URL (cleanup)
 */
export const revokeFilePreview = (url: string): void => {
  if (url.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
};

/**
 * Extension to MIME type mapping
 */
const EXTENSION_TO_MIME: Record<string, string> = {
  // Images
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  avif: "image/avif",
  // Videos
  mp4: "video/mp4",
  webm: "video/webm",
  mov: "video/quicktime",
  // Audio
  mp3: "audio/mpeg",
  wav: "audio/wav",
  oga: "audio/ogg",
  ogg: "audio/ogg",
  // Documents
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

/**
 * Convert file extension (or MIME type) to MIME type for file picker
 * Handles both extensions ("png", "jpeg") and MIME types ("image/png", "image/jpeg")
 */
export const extensionToMimeType = (ext: string): string => {
  // If already a MIME type, return as is (but normalize to wildcard if needed)
  if (ext.includes("/")) {
    // If it's already a MIME type, convert to wildcard format if needed
    if (ext.startsWith("image/")) return "image/*";
    if (ext.startsWith("video/")) return "video/*";
    if (ext.startsWith("audio/")) return "audio/*";
    return ext;
  }

  // Remove leading dot if present
  const cleanExt = ext.replace(/^\./, "").toLowerCase();

  // Convert extension to MIME type
  const mimeType = EXTENSION_TO_MIME[cleanExt];
  if (mimeType) {
    // Convert to wildcard format for file picker
    if (mimeType.startsWith("image/")) return "image/*";
    if (mimeType.startsWith("video/")) return "video/*";
    if (mimeType.startsWith("audio/")) return "audio/*";
    return mimeType;
  }

  // Unknown extension, return as is
  return ext;
};

/**
 * Convert array of extensions/MIME types to allowed types for file picker
 */
export const convertExtensionsToAllowedTypes = (
  extensions?: string[]
): string[] => {
  if (!extensions || extensions.length === 0) {
    return ["*"];
  }

  const allowedTypes = extensions.map(extensionToMimeType);
  // Remove duplicates
  return Array.from(new Set(allowedTypes));
};
